import { useMemo } from "react";

/**
 * Extract the underlying ticker prefix from an Alpaca option symbol.
 * e.g. "SPY260804P00760000" -> "SPY"
 *      "QQQ260804C00400000" -> "QQQ"
 *      "AAPL" -> "AAPL"
 */
function normalizeStrategy(strat) {
    if (!strat) return "NO_STRATEGY";
    const s = String(strat).trim().toUpperCase();
    if (s === "NO_STRATEGY" || s === "N/A" || s === "NONE" || s === "" || s === "SIMPLE" || s === "N/A STRATEGY") {
        return "NO_STRATEGY";
    }
    return s;
}

function getAlpacaUnderlying(symbol) {
    if (!symbol) return null;
    const s = symbol.toUpperCase().trim();
    const match = s.match(/^([A-Z]+)\d/);
    if (match) return match[1];
    return s;
}

export function useAlpacaOrdersView(orders, users, positions) {
    return useMemo(() => {
        const alpacaUsers = (users || []).filter(u => u && String(u.broker).toLowerCase() === "alpaca");
        const alpacaUserIds = new Set(alpacaUsers.map(u => String(u.client_id).toLowerCase()));

        const alpacaPositions = (positions || []).filter(p => p && (
            String(p.broker).toLowerCase() === "alpaca" ||
            alpacaUserIds.has(String(p.clientId || p.client_id).toLowerCase())
        ));
        const alpacaOrders = (orders || []).filter(o => o && (
            String(o.broker).toLowerCase() === "alpaca" ||
            alpacaUserIds.has(String(o.clientId || o.client_id).toLowerCase())
        ));

        // 1. Build position map to identify SL orders
        const detailedPositionMap = {};
        alpacaPositions.forEach(pos => {
            const clientId = pos.clientId || pos.client_id;
            if (!clientId) return;
            const strat = normalizeStrategy(pos.strategy);
            const cleanSym = (pos.symbol || "").toString().trim().toUpperCase();
            const key = `${clientId}_${cleanSym}_${strat}`;
            detailedPositionMap[key] = parseFloat(pos.netQty ?? pos.net_qty ?? pos.qty ?? 0);
        });

        // 2. Group active orders by Client + Underlying Asset
        const clientAssetMap = {};
        alpacaUsers.forEach(u => {
            clientAssetMap[u.client_id] = {};
        });

        const activeAssetsSet = new Set();
        const rawValidOrders = [];

        alpacaOrders.forEach(o => {
            const clientId = o.clientId || o.client_id;
            if (!clientId) return;

            if (!clientAssetMap[clientId]) clientAssetMap[clientId] = {};

            const statusStr = String(o.status || "").toLowerCase();
            const statusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
            const isPending = [
                "new", "partially_filled", "accepted", "pending_new",
                "held", "pending_replace", "pending", "open", "not sent"
            ].includes(statusStr) || statusNo === 6;
            if (!isPending) return;

            const sideStr = String(o.side || "").toLowerCase();
            const isBuy = sideStr === "buy" || sideStr === "1";
            const isSell = sideStr === "sell" || sideStr === "2" || sideStr === "-1";
            if (!isBuy && !isSell) return;

            // Exclude SL orders
            const strategy = normalizeStrategy(o.strategy);
            const cleanSym = (o.symbol || "").toString().trim().toUpperCase();
            const posKey = `${clientId}_${cleanSym}_${strategy}`;
            const posNetQty = detailedPositionMap[posKey];
            if (posNetQty !== undefined && posNetQty !== 0) {
                if ((posNetQty > 0 && isSell) || (posNetQty < 0 && isBuy)) return;
            }

            const asset = getAlpacaUnderlying(o.symbol);
            if (!asset) return;

            activeAssetsSet.add(asset);
            rawValidOrders.push(o);

            if (!clientAssetMap[clientId][asset]) {
                clientAssetMap[clientId][asset] = {
                    qty: 0,
                    buyQty: 0,
                    sellQty: 0,
                    maxQty: 0,
                    orders: []
                };
            }

            const item = clientAssetMap[clientId][asset];
            const qty = Math.abs(parseFloat(o.qty || 0));
            if (isBuy) item.buyQty += qty;
            if (isSell) item.sellQty += qty;
            item.maxQty = Math.max(item.buyQty, item.sellQty);
            item.qty = item.maxQty;
            item.orders.push(o);
        });

        // 3. Build Row Data
        const data = [];
        Object.entries(clientAssetMap).forEach(([clientId, assets]) => {
            const user = alpacaUsers.find(u => u.client_id === clientId);
            const multiplier = user ? parseFloat(user.multiplier || 1) : 1;

            const rowData = {
                clientId,
                clientName: user ? (user.name || clientId) : `Client ${clientId}`,
                status: user ? user.status : "unknown",
                broker: "alpaca",
                multiplier
            };

            let hasAnyOrders = false;
            activeAssetsSet.forEach(asset => {
                const assetData = assets[asset];
                if (assetData && (assetData.buyQty > 0 || assetData.sellQty > 0)) {
                    hasAnyOrders = true;
                    rowData[asset] = {
                        multiplier,
                        qty: assetData.maxQty,
                        buyQty: assetData.buyQty,
                        sellQty: assetData.sellQty,
                        orders: assetData.orders
                    };
                } else {
                    rowData[asset] = null;
                }
            });

            if (hasAnyOrders) {
                data.push(rowData);
            }
        });

        const activeAssets = Array.from(activeAssetsSet).sort();

        // 4. Calculate symbolStrategyBaseQty
        const symbolStrategyBaseQty = {};
        const ordersByLookupKey = {};

        rawValidOrders.forEach(o => {
            const strategy = normalizeStrategy(o.strategy);
            const sym = (o.symbol || "").toUpperCase().trim();
            const lookupKey = `${sym}_${strategy}`;

            if (!ordersByLookupKey[lookupKey]) {
                ordersByLookupKey[lookupKey] = [];
            }
            ordersByLookupKey[lookupKey].push(o);
        });

        Object.entries(ordersByLookupKey).forEach(([lookupKey, groupOrders]) => {
            const clientQtys = {};
            const clientIdsSet = new Set();
            let symbol = "";
            let strategy = "";

            groupOrders.forEach(o => {
                const clientId = o.clientId || o.client_id;
                const sideStr = String(o.side || "").toLowerCase();
                const isBuy = sideStr === "buy" || sideStr === "1";
                const isSell = sideStr === "sell" || sideStr === "2" || sideStr === "-1";
                const qty = Math.abs(parseFloat(o.qty || 0));

                if (!clientQtys[clientId]) clientQtys[clientId] = { buy: 0, sell: 0 };
                if (isBuy) clientQtys[clientId].buy += qty;
                if (isSell) clientQtys[clientId].sell += qty;

                clientIdsSet.add(clientId);
                if (!symbol) symbol = o.symbol;
                if (!strategy || strategy === "N/A") strategy = normalizeStrategy(o.strategy);
            });

            // Normalize and get mode
            const normalizedQtys = [];
            clientIdsSet.forEach(cid => {
                const user = alpacaUsers.find(u => u.client_id === cid);
                const multiplier = user ? parseFloat(user.multiplier || 1) : 1;
                const actQty = Math.max(clientQtys[cid].buy, clientQtys[cid].sell) || 0;
                normalizedQtys.push(actQty / (multiplier > 0 ? multiplier : 1));
            });

            let baseQty = 0;
            if (normalizedQtys.length > 0) {
                const freq = {};
                let maxFreq = 0;
                let mode = normalizedQtys[0];
                normalizedQtys.forEach(n => {
                    freq[n] = (freq[n] || 0) + 1;
                    if (freq[n] > maxFreq) { maxFreq = freq[n]; mode = n; }
                });
                baseQty = mode;
            }

            symbolStrategyBaseQty[lookupKey] = {
                symbol,
                strategy,
                baseQty,
                index: getAlpacaUnderlying(symbol),
                clients: Array.from(clientIdsSet)
            };
        });

        // 5. Base Qty Map (mode of non-zero normalized quantities)
        const baseQtyMap = {};
        activeAssets.forEach(asset => {
            const qtys = [];
            data.forEach(row => {
                const cell = row[asset];
                if (cell && cell.qty > 0) {
                    const m = cell.multiplier > 0 ? cell.multiplier : 1;
                    qtys.push(cell.qty / m);
                }
            });
            if (qtys.length > 0) {
                const freq = {};
                let maxFreq = 0, mode = qtys[0];
                qtys.forEach(n => {
                    freq[n] = (freq[n] || 0) + 1;
                    if (freq[n] > maxFreq) { maxFreq = freq[n]; mode = n; }
                });
                baseQtyMap[asset] = mode;
            }
        });

        return { data, baseQtyMap, activeAssets, symbolStrategyBaseQty };
    }, [orders, users, positions]);
}
